import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

/** Callback ref + element state so TanStack Virtual rebinds after the scroll parent mounts. */
export function useVirtualScrollElement<T extends HTMLElement>(): {
  ref: (node: T | null) => void;
  refObject: RefObject<T | null>;
  element: T | null;
  /** True once the scrollport is in the DOM with a non-zero height. */
  scrollReady: boolean;
} {
  const refObject = useRef<T | null>(null);
  const [element, setElement] = useState<T | null>(null);
  const [scrollReady, setScrollReady] = useState(false);
  /** Latch: avoid toggling scrollReady off during transient 0-height layout (drawer resize). */
  const everReadyRef = useRef(false);
  const ref = useCallback((node: T | null) => {
    refObject.current = node;
    setElement(node);
  }, []);

  useLayoutEffect(() => {
    if (!element) {
      everReadyRef.current = false;
      setScrollReady(false);
      return;
    }
    const check = () => {
      const height = element.getBoundingClientRect().height;
      if (height > 0) {
        everReadyRef.current = true;
        setScrollReady(true);
        return;
      }
      if (!everReadyRef.current) {
        setScrollReady(false);
      }
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return { ref, refObject, element, scrollReady };
}

/**
 * Wait for mounted virtual rows to be measured before revealing the virtual layer.
 *
 * Only resets when ``contentRevision`` changes — not when ``enabled`` flickers
 * during scrollport resize. Does **not** call ``virtualizer.measure()``; that
 * clears the item-size cache and causes estimate-sized spacing flashes.
 */
export function useVirtualizerInitialMeasure(
  enabled: boolean,
  contentRevision: string | number,
): boolean {
  const [measured, setMeasured] = useState(!enabled);
  const prevRevisionRef = useRef(contentRevision);

  useLayoutEffect(() => {
    if (!enabled) {
      setMeasured(true);
      return;
    }
    if (prevRevisionRef.current !== contentRevision) {
      prevRevisionRef.current = contentRevision;
      setMeasured(false);
    }
  }, [enabled, contentRevision]);

  useLayoutEffect(() => {
    if (!enabled || measured) return;
    let frame2 = 0;
    const frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(() => {
        setMeasured(true);
      });
    });
    return () => {
      cancelAnimationFrame(frame1);
      cancelAnimationFrame(frame2);
    };
  }, [enabled, measured, contentRevision]);

  return measured;
}
